import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { ApiError, iotApi } from '../lib/iotApi';

type FatProof = 'DIGITAL_PROOF' | 'OPERATOR_PROOF' | 'NO_PROOF';
type FatStatus = 'NOT_TESTED' | 'TESTING' | 'PASS' | 'FAIL' | 'PARTIAL';

interface FatCapabilities {
  runtime_supported: boolean;
  block_reason?: string | null;
  identity_check: boolean;
  uplink_test: boolean;
  decoder_test: boolean;
  telemetry_required_measurement_ids: string[];
  freshness_max_seconds: number;
  command_test: boolean;
  command_type?: string | null;
  channels: string[];
  safe_actions: string[];
  feedback_supported: boolean;
  protocol_model?: string | null;
  protocol_codec_version?: string | null;
  uplink_f_ports: number[];
}

interface FatEquipment {
  node_id: string;
  physical_asset_id?: string | null;
  name?: string | null;
  model?: string | null;
  dev_eui?: string | null;
  ttn_device_id?: string | null;
  last_seen_at?: string | null;
  operational_mode: string;
  bench_test_authorized: boolean;
  fat_status: FatStatus;
  capabilities: FatCapabilities;
}

interface FatSession {
  id: string;
  node_id: string;
  status: FatStatus;
  started_at?: string;
  tests?: Array<Record<string, unknown>>;
}

interface PendingFatCommand {
  sessionId: string;
  nodeId: string;
  channel: string;
  action: 'on' | 'off';
  idempotencyKey: string;
}

interface PersistedPendingFatCommand extends PendingFatCommand {
  principalId: string;
  expiresAtEpochMs: number;
}

type UncertainFatCommands = Record<string, PersistedPendingFatCommand>;

const UNCERTAIN_COMMANDS_STORAGE_PREFIX = 'agrisafe:fat:uncertain-commands:v2:';
const UNCERTAIN_COMMAND_TTL_MS = 6 * 24 * 60 * 60 * 1000;

const uncertainStoragePrefix = (principalId: string): string =>
  `${UNCERTAIN_COMMANDS_STORAGE_PREFIX}${encodeURIComponent(principalId)}:`;

const uncertainChannelStorageKey = (principalId: string, channelKey: string): string =>
  `${uncertainStoragePrefix(principalId)}${encodeURIComponent(channelKey)}`;

const intentCreatedAtEpochMs = (key: string): number | null => {
  const parts = key.split('-');
  if (parts.length < 3 || parts[0] !== 'fat') return null;
  const value = Number.parseInt(parts[1], 36);
  return Number.isFinite(value) && value > 0 ? value : null;
};

const persistIntent = (
  intent: PendingFatCommand,
  principalId: string,
  existing?: PersistedPendingFatCommand,
): PersistedPendingFatCommand => {
  const createdAt = intentCreatedAtEpochMs(intent.idempotencyKey) ?? Date.now();
  const hardExpiry = createdAt + UNCERTAIN_COMMAND_TTL_MS;
  return {
    ...intent,
    principalId,
    expiresAtEpochMs: existing
      ? Math.min(existing.expiresAtEpochMs, hardExpiry)
      : hardExpiry,
  };
};

const commandChannelKey = (
  command: Pick<PendingFatCommand, 'nodeId' | 'channel'>,
): string => [command.nodeId, command.channel].join('\u0000');

const loadUncertainFatCommands = (principalId: string): UncertainFatCommands => {
  if (typeof window === 'undefined' || !principalId) return {};
  const prefix = uncertainStoragePrefix(principalId);
  const now = Date.now();
  const loaded: UncertainFatCommands = {};
  try {
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const storageKey = window.localStorage.key(index);
      if (!storageKey || !storageKey.startsWith(prefix)) continue;
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) continue;
      const value = JSON.parse(raw) as PersistedPendingFatCommand;
      if (
        !value
        || typeof value !== 'object'
        || value.principalId !== principalId
        || !Number.isFinite(value.expiresAtEpochMs)
        || value.expiresAtEpochMs <= now
      ) continue;
      const channelKey = commandChannelKey(value);
      if (storageKey !== uncertainChannelStorageKey(principalId, channelKey)) continue;
      loaded[channelKey] = value;
    }
    return loaded;
  } catch {
    return {};
  }
};

const writeUncertainChannel = (
  principalId: string,
  channelKey: string,
  intent: PersistedPendingFatCommand,
): void => {
  if (typeof window === 'undefined' || !principalId) return;
  window.localStorage.setItem(
    uncertainChannelStorageKey(principalId, channelKey),
    JSON.stringify(intent),
  );
};

const clearUncertainChannel = (
  principalId: string,
  channelKey: string,
  completedIdempotencyKey: string,
): boolean => {
  if (typeof window === 'undefined' || !principalId) return true;
  try {
    const storageKey = uncertainChannelStorageKey(principalId, channelKey);
    const raw = window.localStorage.getItem(storageKey);
    if (raw && JSON.parse(raw).idempotencyKey !== completedIdempotencyKey) return false;
    window.localStorage.removeItem(storageKey);
    return true;
  } catch {
    return false;
  }
};

const idempotencyKey = (): string =>
  `fat-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

const intentHasExpired = (intent: PendingFatCommand): boolean => {
  const createdAt = intentCreatedAtEpochMs(intent.idempotencyKey);
  const hardExpiry = createdAt === null ? 0 : createdAt + UNCERTAIN_COMMAND_TTL_MS;
  const persistedExpiry = 'expiresAtEpochMs' in intent
    ? Number(intent.expiresAtEpochMs) : hardExpiry;
  return !Number.isFinite(persistedExpiry)
    || Math.min(persistedExpiry, hardExpiry) <= Date.now();
};

const errorDetail = (err: unknown): string =>
  err instanceof ApiError ? err.detail : String(err);

const isUncertainCommandError = (err: unknown): boolean => err instanceof ApiError && (
  err.semanticCode === 'TTN_OUTCOME_UNCERTAIN'
  || err.semanticCode === 'FAT_SESSION_AUDIT_PERSISTENCE_FAILED'
  || err.semanticCode === 'request_aborted'
  || err.semanticCode === 'network_error'
  || err.detail.includes('TTN_OUTCOME_UNCERTAIN')
  || err.detail.includes('FAT_SESSION_AUDIT_PERSISTENCE_FAILED')
);

// Reset all session, confirmation and error state synchronously at an auth boundary.
const AgriSafeFatR1: React.FC = () => {
  const { user } = useAuth();
  const scope = JSON.stringify([user?.id, user?.org_id, user?.role]);
  return <FatPrincipalPage key={scope} />;
};

const FatPrincipalPage: React.FC = () => {
  const { t } = useTranslation(['agrisafeR1', 'fat']);
  const { user } = useAuth();
  const fatRole = typeof user?.role === 'string' ? user.role.trim().toLowerCase() : '';
  const principalId = typeof user?.id === 'string' ? user.id : '';
  const canManageAuthorization = ['owner', 'admin', 'org_admin', 'coop_admin'].includes(fatRole);
  const [equipment, setEquipment] = useState<FatEquipment[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [sessions, setSessions] = useState<FatSession[]>([]);
  const [activeSession, setActiveSession] = useState<FatSession | null>(null);
  const [pending, setPending] = useState<PendingFatCommand | null>(null);
  const [uncertainCommands, setUncertainCommands] = useState<UncertainFatCommands>(() => loadUncertainFatCommands(principalId));
  const [lastCommand, setLastCommand] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const selectedIdRef = useRef('');
  const sessionRequestSeq = useRef(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      selectedIdRef.current = '';
      sessionRequestSeq.current += 1;
    };
  }, []);

  const selected = useMemo(
    () => equipment.find((row) => row.node_id === selectedId) ?? null,
    [equipment, selectedId],
  );

  useEffect(() => {
    setUncertainCommands(loadUncertainFatCommands(principalId));
  }, [principalId]);

  useEffect(() => {
    if (typeof window === 'undefined' || !principalId) return;
    const prefix = uncertainStoragePrefix(principalId);
    const onStorage = (event: StorageEvent) => {
      if (event.key?.startsWith(prefix)) {
        setUncertainCommands(loadUncertainFatCommands(principalId));
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [principalId]);

  const loadEquipment = useCallback(async () => {
    const rows = await iotApi.getJson<FatEquipment[]>('/iot/fat/equipment');
    if (!mounted.current) return;
    const safeRows = Array.isArray(rows) ? rows : [];
    setEquipment(safeRows);
    setSelectedId((current) => current || safeRows[0]?.node_id || '');
  }, []);

  const loadSessions = useCallback(async (nodeId: string) => {
    if (!nodeId || !mounted.current) return;
    const requestSeq = ++sessionRequestSeq.current;
    const rows = await iotApi.getJson<FatSession[]>(
      `/iot/fat/equipment/${encodeURIComponent(nodeId)}/sessions`,
    );
    if (
      requestSeq !== sessionRequestSeq.current
      || selectedIdRef.current !== nodeId
    ) {
      return;
    }
    const safeRows = Array.isArray(rows) ? rows : [];
    setSessions(safeRows);
    setActiveSession((current) => {
      if (current?.node_id === nodeId) {
        return safeRows.find((row) => row.id === current.id) ?? safeRows[0] ?? null;
      }
      return safeRows[0] ?? null;
    });
  }, []);

  const refreshProjection = useCallback(async (nodeId: string) => {
    await Promise.all([loadEquipment(), loadSessions(nodeId)]);
  }, [loadEquipment, loadSessions]);

  useEffect(() => {
    let active = true;
    loadEquipment()
      .catch((err) => {
        if (active) setError(errorDetail(err));
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [loadEquipment]);

  useEffect(() => {
    selectedIdRef.current = selectedId;
    sessionRequestSeq.current += 1;
    setSessions([]);
    setActiveSession(null);
    setPending(null);
    setLastCommand(null);
    setError(null);
    if (!selectedId) return;
    loadSessions(selectedId).catch((err) => {
      if (selectedIdRef.current === selectedId) setError(errorDetail(err));
    });
  }, [selectedId, loadSessions]);

  const authorize = async (enabled: boolean) => {
    if (!selected || !canManageAuthorization) return;
    const nodeId = selected.node_id;
    setError(null);
    try {
      await iotApi.put(
        `/iot/fat/equipment/${encodeURIComponent(nodeId)}/authorization`,
        { enabled },
      );
      if (selectedIdRef.current === nodeId) await loadEquipment();
    } catch (err) {
      if (selectedIdRef.current === nodeId) setError(errorDetail(err));
    }
  };

  const startSession = async () => {
    if (!selected) return;
    const nodeId = selected.node_id;
    setError(null);
    try {
      const session = await iotApi.post(
        `/iot/fat/equipment/${encodeURIComponent(nodeId)}/sessions`,
        { profile: 'DEFAULT' },
      ) as FatSession;
      if (selectedIdRef.current !== nodeId) return;
      setLastCommand(null);
      setActiveSession(session);
      await refreshProjection(nodeId);
    } catch (err) {
      if (selectedIdRef.current === nodeId) setError(errorDetail(err));
    }
  };

  const evaluate = async () => {
    if (!activeSession || activeSession.node_id !== selectedIdRef.current) return;
    const nodeId = activeSession.node_id;
    const sessionId = activeSession.id;
    setError(null);
    try {
      const session = await iotApi.post(
        `/iot/fat/sessions/${encodeURIComponent(sessionId)}/evaluate`,
      ) as FatSession;
      if (selectedIdRef.current !== nodeId) return;
      setActiveSession(session);
      await refreshProjection(nodeId);
    } catch (err) {
      if (selectedIdRef.current === nodeId) setError(errorDetail(err));
    }
  };

  const submitCommand = async (intent: PendingFatCommand) => {
    if (!mounted.current || intent.nodeId !== selectedIdRef.current) return;
    setPending(null);
    // The page or dialog may have stayed open beyond the server replay lifetime.
    // Keep the unresolved channel locked; never turn an expired retry into a new intent.
    if (intentHasExpired(intent)) {
      setError('FAT_COMMAND_RETRY_EXPIRED');
      return;
    }
    setError(null);
    const channelKey = commandChannelKey(intent);
    let result: Record<string, unknown>;
    try {
      result = await iotApi.post(
        `/iot/fat/sessions/${encodeURIComponent(intent.sessionId)}/commands`,
        { channel: intent.channel, action: intent.action, confirmed: true },
        intent.idempotencyKey,
      ) as Record<string, unknown>;
    } catch (err) {
      if (isUncertainCommandError(err)) {
        setUncertainCommands((current) => {
          const persisted = persistIntent(intent, principalId, current[channelKey]);
          try {
            writeUncertainChannel(principalId, channelKey, persisted);
          } catch {
            // Storage failure must never unlock the in-memory physical channel guard.
          }
          return { ...current, [channelKey]: persisted };
        });
      }
      if (selectedIdRef.current === intent.nodeId) {
        setError(errorDetail(err));
        try {
          await refreshProjection(intent.nodeId);
        } catch {
          // Preserve the original channel lock and idempotency key.
        }
      }
      return;
    }

    const replayStillRequested = result.idempotency_replay === true && result.status === 'REQUESTED';
    setUncertainCommands((current) => {
      if (replayStillRequested) {
        const persisted = persistIntent(intent, principalId, current[channelKey]);
        try {
          writeUncertainChannel(principalId, channelKey, persisted);
        } catch {
          // Storage failure must never unlock the in-memory physical channel guard.
        }
        return { ...current, [channelKey]: persisted };
      }
      if (!current[channelKey] || current[channelKey].idempotencyKey !== intent.idempotencyKey) return current;
      if (!clearUncertainChannel(principalId, channelKey, intent.idempotencyKey)) {
        // A newer intent may have been written before its storage event arrives.
        // Reconcile it without dropping any in-memory guards on storage failure.
        return { ...current, ...loadUncertainFatCommands(principalId) };
      }
      const next = { ...current };
      delete next[channelKey];
      return next;
    });

    if (selectedIdRef.current !== intent.nodeId) return;
    setLastCommand({
      ...result,
      fat_node_id: intent.nodeId,
      fat_session_id: intent.sessionId,
    });

    try {
      await refreshProjection(intent.nodeId);
    } catch (err) {
      if (selectedIdRef.current === intent.nodeId) setError(errorDetail(err));
    }
  };

  const confirmCommand = async () => {
    if (!pending) return;
    await submitCommand(pending);
  };

  const operatorProof = async (confirmed: boolean) => {
    if (
      !activeSession
      || activeSession.node_id !== selectedIdRef.current
      || typeof lastCommand?.command_id !== 'string'
      || lastCommand.fat_node_id !== activeSession.node_id
      || lastCommand.fat_session_id !== activeSession.id
    ) return;
    const nodeId = activeSession.node_id;
    const sessionId = activeSession.id;
    const commandId = lastCommand.command_id;
    setError(null);
    try {
      await iotApi.post(
        `/iot/fat/sessions/${encodeURIComponent(sessionId)}/operator-proof`,
        { command_id: commandId, confirmed },
      );
      if (selectedIdRef.current !== nodeId) return;
      setLastCommand((current) => (
        current?.command_id === commandId
          ? { ...current, proof: 'OPERATOR_PROOF', operator_confirmed: confirmed }
          : current
      ));
      await refreshProjection(nodeId);
    } catch (err) {
      if (selectedIdRef.current === nodeId) setError(errorDetail(err));
    }
  };

  if (loading) {
    return <div className="h-40 animate-pulse rounded-2xl bg-[var(--gray-100)]" data-testid="fat-loading" />;
  }

  const sessionMatchesSelection = activeSession?.node_id === selected?.node_id;
  const proofMatchesSelection = Boolean(
    lastCommand && lastCommand.fat_node_id === selected?.node_id,
  );

  return (
    <div className="space-y-4 pb-24 lg:pb-6" data-testid="fat-page">
      <header className="rounded-2xl border border-[var(--border-light)] bg-[var(--surface)] p-4 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">{t('fat:badge')}</p>
        <h1 className="mt-1 text-xl font-semibold text-[var(--text-primary)]">{t('pumping.diagnostic.title')}</h1>
        <p className="mt-1 text-sm text-[var(--text-secondary)]">{t('pumping.diagnostic.subtitle')}</p>
      </header>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900" data-testid="fat-error">
          {error}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
        <section className="rounded-2xl border border-[var(--border-light)] bg-[var(--surface)] p-3 shadow-sm">
          <h2 className="px-1 text-sm font-semibold text-[var(--text-primary)]">{t('nav.equipment')}</h2>
          <ul className="mt-2 space-y-2" data-testid="fat-equipment-list">
            {equipment.map((row) => (
              <li key={row.node_id}>
                <button
                  type="button"
                  onClick={() => setSelectedId(row.node_id)}
                  className="w-full rounded-xl border border-[var(--border-light)] px-3 py-2 text-left text-sm"
                  data-testid={`fat-equipment-${row.node_id}`}
                >
                  <span className="block font-medium text-[var(--text-primary)]">{row.name || row.node_id}</span>
                  <span className="block text-xs text-[var(--text-secondary)]">
                    {row.model} · {row.fat_status}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>

        {selected && (
          <div className="space-y-4">
            <section className="rounded-2xl border border-[var(--border-light)] bg-[var(--surface)] p-4 shadow-sm" data-testid="fat-capabilities">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="font-semibold text-[var(--text-primary)]">{selected.name || selected.node_id}</h2>
                  <p className="text-xs text-[var(--text-secondary)]">
                    {selected.node_id} · {selected.model} · {selected.capabilities.protocol_model}@{selected.capabilities.protocol_codec_version}
                  </p>
                </div>
                <span className="text-xs font-semibold text-[var(--text-secondary)]">{selected.fat_status}</span>
              </div>

              {!selected.capabilities.runtime_supported ? (
                <p className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="fat-unsupported">
                  {selected.capabilities.block_reason || 'FAT_RUNTIME_PROTOCOL_UNSUPPORTED'}
                </p>
              ) : (
                <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
                  <div><dt className="text-[var(--text-secondary)]">{t('fat:runtime')}</dt><dd>{selected.capabilities.runtime_supported ? 'SUPPORTED' : 'UNSUPPORTED'}</dd></div>
                  <div><dt className="text-[var(--text-secondary)]">{t('fat:uplinkFPort')}</dt><dd>{selected.capabilities.uplink_f_ports.join(', ') || '—'}</dd></div>
                  <div><dt className="text-[var(--text-secondary)]">{t('fat:telemetry')}</dt><dd>{selected.capabilities.telemetry_required_measurement_ids.join(', ') || '—'}</dd></div>
                  <div><dt className="text-[var(--text-secondary)]">{t('fat:command')}</dt><dd>{selected.capabilities.command_type || 'PASSIVE'}</dd></div>
                </dl>
              )}

              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => void authorize(!selected.bench_test_authorized)}
                  disabled={!canManageAuthorization}
                  className="min-h-[40px] rounded-xl border border-[var(--border-medium)] px-3 py-2 text-sm font-medium disabled:opacity-40"
                  data-testid="fat-toggle-authorization"
                >
                  {selected.bench_test_authorized ? t('fat:authorized') : t('fat:locked')}
                </button>
                <button
                  type="button"
                  onClick={() => void startSession()}
                  disabled={!selected.bench_test_authorized || !selected.capabilities.runtime_supported}
                  className="min-h-[40px] rounded-xl bg-[var(--color-primary)] px-3 py-2 text-sm font-semibold text-white disabled:opacity-40"
                  data-testid="fat-start-session"
                >
                  {t('dashboard.pump.confirm')}
                </button>
                <button
                  type="button"
                  onClick={() => void evaluate()}
                  disabled={!sessionMatchesSelection}
                  className="min-h-[40px] rounded-xl border border-[var(--border-medium)] px-3 py-2 text-sm font-medium disabled:opacity-40"
                  data-testid="fat-evaluate"
                >
                  {t('dashboard.lastData')}
                </button>
              </div>
            </section>

            {sessionMatchesSelection && selected.capabilities.command_test && (
              <section className="rounded-2xl border border-[var(--border-light)] bg-[var(--surface)] p-4 shadow-sm" data-testid="fat-actuator-profile">
                <h2 className="font-semibold text-[var(--text-primary)]">{selected.capabilities.command_type}</h2>
                <div className="mt-3 space-y-2">
                  {selected.capabilities.channels.map((channel) => {
                    const channelKey = commandChannelKey({ nodeId: selected.node_id, channel });
                    const uncertainIntent = uncertainCommands[channelKey];
                    return (
                      <div key={channel} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[var(--border-light)] px-3 py-2">
                        <span className="text-sm font-medium">{channel}</span>
                        <div className="flex gap-2">
                          {selected.capabilities.safe_actions.map((action) => {
                            const target = {
                              sessionId: activeSession!.id,
                              nodeId: selected.node_id,
                              channel,
                              action: action as 'on' | 'off',
                            };
                            const isOtherActionLocked = Boolean(uncertainIntent && uncertainIntent.action !== action);
                            return (
                              <button
                                key={action}
                                type="button"
                                disabled={isOtherActionLocked || Boolean(uncertainIntent && intentHasExpired(uncertainIntent))}
                                onClick={() => {
                                  if (uncertainIntent) {
                                    if (uncertainIntent.action === action) setPending(uncertainIntent);
                                    return;
                                  }
                                  setPending({ ...target, idempotencyKey: idempotencyKey() });
                                }}
                                className="min-h-[40px] rounded-lg border border-[var(--border-medium)] px-3 py-1.5 text-xs font-semibold disabled:opacity-40"
                                data-testid={`fat-command-${channel}-${action}`}
                                aria-label={uncertainIntent && uncertainIntent.action === action ? t('fat:retrySameIntentAria', { action }) : action}
                              >
                                {uncertainIntent && uncertainIntent.action === action
                                  ? t('fat:retrySameIntentButton', { action: action.toUpperCase() })
                                  : action.toUpperCase()}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {!selected.capabilities.command_test && selected.capabilities.runtime_supported && (
              <section className="rounded-2xl border border-[var(--border-light)] bg-[var(--surface)] p-4 shadow-sm" data-testid="fat-passive-profile">
                <h2 className="font-semibold text-[var(--text-primary)]">{t('dashboard.liveMeasures')}</h2>
                <p className="mt-2 text-sm text-[var(--text-secondary)]">
                  {selected.capabilities.telemetry_required_measurement_ids.join(', ') || t('dashboard.noDataAvailable')}
                </p>
                <p className="mt-1 text-xs text-[var(--text-secondary)]">
                  {t('dashboard.lastCommunication')}: {selected.last_seen_at || '—'}
                </p>
              </section>
            )}

            {proofMatchesSelection && lastCommand && (
              <section className="rounded-2xl border border-[var(--border-light)] bg-[var(--surface)] p-4 shadow-sm" data-testid="fat-proof">
                <p className="text-sm text-[var(--text-primary)]">
                  {String(lastCommand.status || '')} · {String((lastCommand.proof as FatProof | undefined) || 'NO_PROOF')}
                </p>
                {typeof lastCommand.command_id === 'string' && (
                  <div className="mt-3 flex gap-2">
                    <button type="button" onClick={() => void operatorProof(true)} className="rounded-lg border px-3 py-2 text-sm">
                      {t('fat:operatorPass')}
                    </button>
                    <button type="button" onClick={() => void operatorProof(false)} className="rounded-lg border px-3 py-2 text-sm">
                      {t('fat:operatorFail')}
                    </button>
                  </div>
                )}
              </section>
            )}

            <section className="rounded-2xl border border-[var(--border-light)] bg-[var(--surface)] p-4 shadow-sm" data-testid="fat-history">
              <h2 className="font-semibold text-[var(--text-primary)]">{t('pumping.history.title')}</h2>
              <ul className="mt-2 space-y-1 text-sm">
                {sessions.map((session) => (
                  <li key={session.id} className="flex justify-between gap-2 border-b border-[var(--border-light)] py-2">
                    <span>{session.started_at || session.id}</span><span>{session.status}</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        )}
      </div>

      {pending && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" data-testid="fat-confirm-dialog">
          <div className="w-full max-w-md rounded-2xl bg-[var(--surface)] p-5 shadow-lg">
            <p className="font-semibold text-[var(--text-primary)]">{pending.nodeId} · {pending.channel} · {pending.action.toUpperCase()}</p>
            {uncertainCommands[commandChannelKey(pending)] && (
              <p className="mt-2 text-sm text-amber-700">
                {t('fat:uncertainRetryNotice')}
              </p>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setPending(null)} className="rounded-xl border px-4 py-2 text-sm">
                {t('parcel.confirm.cancel')}
              </button>
              <button type="button" onClick={() => void confirmCommand()} className="rounded-xl bg-[var(--color-primary)] px-4 py-2 text-sm font-semibold text-white" data-testid="fat-confirm-proceed">
                {t('dashboard.pump.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgriSafeFatR1;
